/**
 * Utility functions for managing "Remember Me" functionality
 * Stores user preference to auto-login for 7 days
 * 
 * SECURITY NOTES:
 * - Only stores username (not tokens or passwords)
 * - Includes device fingerprint to prevent token theft across devices
 * - 7-day expiration enforces periodic re-authentication
 * - MSAL handles actual token security and refresh
 */

const REMEMBER_ME_KEY = 'rememberMe';
const REMEMBER_ME_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

/**
 * Generate a simple device fingerprint for additional security
 * This helps ensure the remember me token is tied to the same browser/device
 */
const getDeviceFingerprint = () => {
    const userAgent = navigator.userAgent;
    const screenResolution = `${window.screen.width}x${window.screen.height}`;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const language = navigator.language;
    
    // Create a simple fingerprint (not cryptographically secure, but adds a layer)
    const fingerprint = `${userAgent}-${screenResolution}-${timezone}-${language}`;
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString(36);
};

/**
 * Save remember me preference with expiration timestamp
 * @param {string} username - The user's username/email
 */
export const saveRememberMe = (username) => {
    const expirationTime = Date.now() + REMEMBER_ME_DURATION;
    const rememberMeData = {
        username,
        expiresAt: expirationTime,
        deviceFingerprint: getDeviceFingerprint(),
        // Add timestamp for auditing
        createdAt: Date.now()
    };
    
    try {
        localStorage.setItem(REMEMBER_ME_KEY, JSON.stringify(rememberMeData));
    } catch (error) {
        console.error('Failed to save remember me preference:', error);
        // Fail silently - user will just need to sign in manually next time
    }
};

/**
 * Get remember me preference if still valid
 * @returns {Object|null} - The remember me data or null if expired/not found
 */
export const getRememberMe = () => {
    const stored = localStorage.getItem(REMEMBER_ME_KEY);
    if (!stored) return null;

    try {
        const data = JSON.parse(stored);
        
        // Check if expired
        if (Date.now() > data.expiresAt) {
            clearRememberMe();
            return null;
        }

        // Verify device fingerprint matches (security check)
        // If fingerprint doesn't match, it might be a different device or browser
        if (data.deviceFingerprint && data.deviceFingerprint !== getDeviceFingerprint()) {
            console.warn('Device fingerprint mismatch - clearing remember me data');
            clearRememberMe();
            return null;
        }

        return data;
    } catch (error) {
        console.error('Error parsing remember me data:', error);
        clearRememberMe();
        return null;
    }
};

/**
 * Clear remember me preference
 */
export const clearRememberMe = () => {
    localStorage.removeItem(REMEMBER_ME_KEY);
};

/**
 * Check if remember me is active and valid
 * @returns {boolean}
 */
export const isRememberMeActive = () => {
    return getRememberMe() !== null;
};
